/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX, KeyboardEvent, WheelEvent } from "react";
import { useCallback, useEffect, useRef } from "react";

import type { ListSelectionDialogProps } from "./ListSelectionDialogProps";
import type { IItemLabelParts } from "./ListSelectionDialogFormatter";
import { splitItemLabel } from "./ListSelectionDialogFormatter";
import CloseIcon from "../CloseIcon";
import "./ListSelectionDialog.scss";

/**
 * Checkmark rendered inside the selected item card — Figma `ic_check_bold`
 * (Dialog › Context=Select, Type=Channel). The export's `#0A0A0C` fill is
 * `currentColor` here so the selected-but-unfocused row can paint it white.
 */
function CheckIcon(): JSX.Element {
  return (
    <svg
      className="list-selection-dialog__check"
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 27.2267L3.72 18.9467L7.49333 15.1733L12 19.6933L25.1733 6.50667L28.9467 10.28L12 27.2267Z" />
    </svg>
  );
}

/**
 * ListSelectionDialog — centered modal overlay for selecting a single value
 * from a vertical list (e.g. Channel).
 *
 * Tapping / clicking an item commits the selection immediately — no OK/Cancel
 * buttons. The component is fully presentational; state is owned by the Redux
 * slice via {@link ListSelectionDialogContainer}.
 *
 * A full-screen scrim dims the background behind the panel. Navigation keeps
 * the focused row in view (smooth where supported) and the mouse wheel scrolls
 * the list directly, so both D-pad and pointer users can reach every option on
 * browser and smart-TV engines alike.
 */
const ListSelectionDialog: FunctionComponent<ListSelectionDialogProps> = (
  props: ListSelectionDialogProps
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
  const TAG: string = "ListSelectionDialog";
  const {
    title,
    description,
    options,
    listFocusIndex,
    selectedIndex,
    showCloseButton,
    onItemHover,
    onItemClick,
    onCloseHover,
    onCancel
  } = props;

  /** Scroll container that holds the option rows. */
  const listRef = useRef<HTMLDivElement | null>(null);
  /** Ref to the currently focused list row, used to keep it in view. */
  const focusedItemRef = useRef<HTMLDivElement | null>(null);

  useEffect((): void => {
    const container: HTMLDivElement | null = listRef.current;
    const item: HTMLDivElement | null = focusedItemRef.current;
    if (container === null || item === null) {
      return;
    }

    const itemTop: number = item.offsetTop;
    const itemBottom: number = itemTop + item.offsetHeight;
    const viewTop: number = container.scrollTop;
    const viewBottom: number = viewTop + container.clientHeight;

    let nextScrollTop: number | null = null;
    if (itemTop < viewTop) {
      // Item is above the viewport — align it to the top edge.
      nextScrollTop = itemTop;
    } else if (itemBottom > viewBottom) {
      // Item is below the viewport — align it to the bottom edge.
      nextScrollTop = itemBottom - container.clientHeight;
    }

    if (nextScrollTop !== null) {
      // "smooth" animates on browsers and modern TV engines; older engines
      // ignore the option and perform an instant, still-correct scroll.
      container.scrollTo({ top: nextScrollTop, behavior: "smooth" });
    }
  }, [listFocusIndex]);

  /**
   * Mouse-wheel scrolling. Adjusts the container's scrollTop directly so it
   * works even when native wheel scrolling is unreliable (embedded/TV engines)
   * or when a parent modal would otherwise swallow the wheel event.
   */
  const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>): void => {
    const container: HTMLDivElement | null = listRef.current;
    if (container === null) {
      return;
    }

    const maxScrollTop: number =
      container.scrollHeight - container.clientHeight;
    if (maxScrollTop <= 0) {
      return; // Nothing to scroll.
    }

    const nextScrollTop: number = Math.max(
      0,
      Math.min(maxScrollTop, container.scrollTop + event.deltaY)
    );

    if (nextScrollTop !== container.scrollTop) {
      // Keep the wheel gesture inside the list rather than bubbling to the
      // page/overlay behind it.
      event.stopPropagation();
      container.scrollTop = nextScrollTop;
    }
  }, []);

  return (
    <div
      className="list-selection-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Full-screen dimmer behind the panel. Clicking it cancels the dialog. */}
      <div
        className="list-selection-dialog__scrim"
        onClick={onCancel}
        aria-hidden="true"
      />

      <div className="list-selection-dialog__panel">
        {showCloseButton === true && (
          <button
            type="button"
            aria-label="Close"
            className="list-selection-dialog__close"
            onClick={onCancel}
            onMouseEnter={onCloseHover}
          >
            <CloseIcon className="list-selection-dialog__close-icon" />
          </button>
        )}

        <h2 className="list-selection-dialog__title">{title}</h2>
        <p className="list-selection-dialog__description">{description}</p>

        <div
          ref={listRef}
          className="list-selection-dialog__list"
          role="listbox"
          aria-label={title}
          onWheel={handleWheel}
        >
          {options.map((label: string, index: number): JSX.Element => {
            const isFocused: boolean = index === listFocusIndex;
            const isSelected: boolean = index === selectedIndex;
            const parts: IItemLabelParts = splitItemLabel(label);
            return (
              <div
                key={`${label}-${index}`}
                ref={isFocused ? focusedItemRef : undefined}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                className={
                  "list-selection-dialog__item" +
                  (isFocused ? " list-selection-dialog__item--focused" : "") +
                  (isSelected ? " list-selection-dialog__item--selected" : "")
                }
                onMouseEnter={(): void => onItemHover(index)}
                onClick={(): void => onItemClick(index)}
                onKeyDown={(event: KeyboardEvent<HTMLDivElement>): void => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onItemClick(index);
                  }
                }}
              >
                <span className="list-selection-dialog__item-name">
                  {parts.name}
                </span>
                {parts.number !== null && (
                  <span className="list-selection-dialog__item-number">
                    {parts.number}
                  </span>
                )}
                {isSelected && <CheckIcon />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ListSelectionDialog;
